import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Copy, Check, X, Trash } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/date";

export default function AdminApplications() {
  const { t } = useLang();
  const [apps, setApps] = useState([]);
  const [approvalResult, setApprovalResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = () => api.get(`/admin/applications${statusFilter ? `?status=${statusFilter}` : ""}`).then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); }, [statusFilter]);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/applications/${id}/approve`, {});
      setApprovalResult(data);
      toast.success("Approved");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const reject = async (id) => {
    try {
      await api.post(`/admin/applications/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete application from ${a.name} ${a.surname}?\n\nThis removes the record but does NOT delete an already-created ambassador (if approved).`)) return;
    try {
      await api.delete(`/admin/applications/${a.id}`);
      toast.success("Application deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl">{t.admin.applications}</h1>
        <div className="flex gap-2">
          {["pending", "approved", "rejected", ""].map((s) => (
            <button key={s || "all"} data-testid={`filter-${s || "all"}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs ${statusFilter === s ? "bg-[#f4a261] text-[#0b1521]" : "bg-white/5 border border-white/10"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {approvalResult && (
        <div data-testid="approval-result" className="rounded-2xl bg-[#f4a261] text-[#0b1521] p-6 relative">
          <button onClick={() => setApprovalResult(null)} className="absolute top-4 right-4"><X size={16} /></button>
          <div className="text-xs uppercase tracking-widest">{t.admin.generatedPassword}</div>
          <div className="mt-2 flex items-center gap-3">
            <code data-testid="generated-password" className="text-2xl font-mono">{approvalResult.generated_password}</code>
            <button onClick={() => { navigator.clipboard.writeText(approvalResult.generated_password); toast.success("Copied"); }} className="p-2 rounded-full bg-[#0b1521] text-white">
              <Copy size={14} />
            </button>
          </div>
          <div className="text-sm mt-3">{t.admin.shareWithApplicant}</div>
        </div>
      )}

      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        {apps.length === 0 ? (
          <div className="p-10 text-center text-sm text-white/60">No applications.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
              <tr>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Phone</th>
                <th className="text-left p-4">Submitted</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} data-testid={`app-row-${a.id}`} className="border-b border-white/5 last:border-0">
                  <td className="p-4">{a.name} {a.surname}</td>
                  <td className="p-4 text-white/70">{a.email}</td>
                  <td className="p-4 text-white/70">{a.phone}</td>
                  <td className="p-4 text-xs text-white/70">{formatDateTime(a.created_at)}</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full bg-white/5 text-xs">{a.status}</span></td>
                  <td className="p-4">
                    {a.status === "pending" ? (
                      <div className="flex gap-2 items-center">
                        <button data-testid={`approve-${a.id}`} onClick={() => approve(a.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#f4a261] text-[#0b1521] text-xs">
                          <Check size={12} weight="bold" /> {t.admin.approve}
                        </button>
                        <button data-testid={`reject-${a.id}`} onClick={() => reject(a.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 text-xs">
                          <X size={12} weight="bold" /> {t.admin.reject}
                        </button>
                        <button data-testid={`delete-app-${a.id}`} onClick={() => remove(a)} title="Delete application" className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10">
                          <Trash size={12} />
                        </button>
                      </div>
                    ) : (
                      <button data-testid={`delete-app-${a.id}`} onClick={() => remove(a)} title="Delete application" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10">
                        <Trash size={12} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
